import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [club, setClub] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    jersey_number: "",
    position: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/invite/${token}/verify`);
        if (!res.ok) throw new Error("bad");
        setClub(await res.json());
      } catch (e) {
        setError("Lien d'invitation invalide ou expiré");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/invite/${token}/register`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
          position: form.position || null,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "erreur");
      }
      await refreshUser();
      toast.success("Bienvenue dans le club !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F17]">
        <div className="text-slate-400 font-mono-stat uppercase text-sm">Chargement...</div>
      </div>
    );
  }
  if (error || !club) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] p-6">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <div className="font-display font-black text-2xl uppercase mb-2">{error}</div>
          <div className="text-slate-400 text-sm">
            Demandez un nouveau lien à votre coach.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0B0F17] tactical-grid">
      <div className="w-full max-w-lg bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-8 slide-up">
        <div className="flex items-center gap-3 mb-6">
          {club.logo_url ? (
            <img
              src={club.logo_url}
              alt="Logo"
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#E5B85C] flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#0B0F17]" strokeWidth={2.5} />
            </div>
          )}
          <div>
            <div className="text-[10px] font-mono-stat uppercase tracking-[0.25em] text-[#E5B85C]">
              Invitation
            </div>
            <div className="font-display font-black text-2xl uppercase">{club.club_name}</div>
          </div>
        </div>
        <h2 className="font-display font-black text-3xl uppercase tracking-tight mb-2">
          Rejoins l'équipe
        </h2>
        <p className="text-sm text-slate-400 mb-6">
          Crée ton compte joueur en 30 secondes.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Nom complet">
            <input
              data-testid="invite-name-input"
              required
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
              placeholder="Kylian Dupont"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Email">
              <input
                data-testid="invite-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                placeholder="toi@club.fr"
              />
            </Field>
            <Field label="Mot de passe">
              <input
                data-testid="invite-password-input"
                required
                minLength={6}
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
                placeholder="6+ caractères"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="N° maillot">
              <input
                data-testid="invite-jersey-input"
                type="number"
                min="1"
                max="99"
                value={form.jersey_number}
                onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
              />
            </Field>
            <Field label="Poste">
              <input
                data-testid="invite-position-input"
                type="text"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
                placeholder="Attaquant"
              />
            </Field>
          </div>
          <button
            data-testid="invite-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg disabled:opacity-50"
          >
            {submitting ? "Création..." : "Rejoindre le club"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
